<template>
  <div class="modal-card">
    <b-loading :isFullPage="false" :active="isLoading" />
    <header class="modal-card-head">
      <span class="modal-card-title is-size-5 has-text-centered">{{
        App.ModalRevertNoEffect.TITLE
      }}</span>
      <div class="close-button cursor-pointer" @click.stop="$emit('close')">
        <b-icon icon="close" />
      </div>
    </header>

    <ValidationObserver
      v-slot="{ handleSubmit }"
      tag="div"
      class="modal-content-container"
    >
      <section class="spacing-p-10">
        <ValidationProvider
          v-slot="{ errors }"
          rules="required"
          class="column is-12"
        >
          <b-field
            :label="General.REASON"
            customClass="has-text-color-7"
            :type="errors.length ? 'is-refused' : ''"
          >
            <b-dropdown v-model="reasonOption" aria-role="menu">
              <template slot="trigger">
                <b-button
                  slot="trigger"
                  class="input level"
                  :type="errors.length ? 'is-refused' : 'is-light'"
                  iconRight="chevron-down"
                  outlined
                >
                  <span>{{ reasonOption[1] || General.SELECT_AN_REASON }}</span>
                </b-button>
              </template>

              <b-dropdown-item
                v-for="option in optionsReason"
                :key="option[0]"
                aria-role="listitem"
                :value="option"
              >
                {{ option[1] }}
              </b-dropdown-item>
            </b-dropdown>
            <template #message>
              <ErrorField :errors="errors" />
            </template>
          </b-field>
        </ValidationProvider>
        <ValidationProvider
          v-slot="{ errors }"
          rules="required|min:60|max:200"
          class="column is-12"
        >
          <b-field
            :label="General.DESCRIPTION"
            customClass="has-text-color-7"
            :type="errors.length ? 'is-refused' : ''"
          >
            <b-input
              v-model="message"
              maxlength="200"
              type="textarea"
              :placeholder="App.ModalRevertNoEffect.DESCRIPITION_PLACEHOLDER"
            >
            </b-input>
            <template #message>
              <ErrorField :errors="errors" />
            </template>
          </b-field>
        </ValidationProvider>
        <div class="is-size-7">
          <ListAttachments
            v-model="currentPageNoEditable"
            class="column custom-width"
            :canSign="false"
            :showModalUpload="showModalUpload"
            :showPDF="true"
            :isRevertNoEffect="true"
          />
        </div>
      </section>

      <footer class="modal-card-foot custom-align-left">
        <div class="buttons">
          <b-button
            :type="isContrast ? 'is-white-dark' : 'is-primary'"
            @click="handleSubmit(confirmAction)"
          >
            {{ General.SAVE.toUpperCase() }}
          </b-button>
          <b-button
            :type="isContrast ? 'is-white-dark' : 'is-primary'"
            class="is-outlined"
            @click="$emit('close')"
          >
            {{ General.CANCEL.toUpperCase() }}
          </b-button>
        </div>
      </footer>
    </ValidationObserver>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";

import { App, General } from "../../utils/constants";
import ModalAttachments from "@/views/App/ModalAttachments";
import ListAttachments from "@/components/ListAttachments";
import { ATTACHEMENT_MAX_COUNT } from "@/utils/enumerate";
import ModalWarning from "./ModalWarning.vue";

export default {
  name: "ModalRevertNoEffect",
  components: {
    ListAttachments
  },

  props: {
    processFileId: {
      type: String,
      default: null
    },
    clearSelection: {
      type: Function,
      default: () => null
    },
    onClose: {
      type: Function,
      default: null
    },
    processId: {
      type: String,
      required: true
    },
    listProcess: {
      type: Array,
      required: true
    }
  },

  data() {
    return {
      App,
      General,
      isLoading: false,
      reasonOption: "",
      message: "",
      currentPageNoEditable: 1
    };
  },

  computed: {
    ...mapGetters({
      needUpdateAttachments: "needUpdateAttachments",
      socketGetDocumentToUpdate: "socketGetDocumentToUpdate",
      getProcessFileSize: "getProcessFileSize",
      getDraftFilesSize: "getDraftFilesSize",
      isContrast: "isContrast"
    }),
    optionsReason() {
      return Object.entries(this.App.ModalRevertNoEffect.REASONS);
    },
    getCurrentSizeProcess() {
      return (
        this.getDraftFilesSize("processAttachmentFiles") +
        this.getProcessFileSize
      );
    }
  },

  watch: {
    async currentPageNoEditable() {
      this.isLoading = true;
      await this.loadProcessFile();
      this.isLoading = false;
    },
    needUpdateAttachments(file) {
      this.updateListAttachmentsAction(file);
    },
    socketGetDocumentToUpdate: {
      async handler(documentToUpdate) {
        if (
          documentToUpdate.isRevertNoEffect &&
          documentToUpdate.files_id?.length
        ) {
          await this.requestProcessFiles(documentToUpdate);
          this.clearDocumentToUpdate();
        }
      },
      deep: true
    }
  },

  async beforeMount() {
    this.isLoading = true;
    await Promise.allSettled([
      this.fileTypesRequest({ state: true }),
      this.loadProcessFile(),
      this.getAllowedExtensions(),
      this.requestProcessFileSize({ processId: this.processId })
    ]);
    this.isLoading = false;
  },

  methods: {
    ...mapActions({
      processFileRequest: "getListAttachmentRequest",
      setProcessFileRevertNoEffect: "setProcessFileRevertNoEffect",
      fileTypesRequest: "fileTypesRequest",
      requestProcessFileSize: "requestProcessFileSize",
      getAllowedExtensions: "getAllowedExtensions",
      updateListAttachmentsAction: "updateListAttachmentsAction",
      requestProcessFiles: "processFilesFilteredRequest"
    }),
    ...mapMutations({ clearDocumentToUpdate: "clearDocumentToUpdate" }),
    async confirmAction() {
      this.$buefy.modal.open({
        parent: this,
        component: ModalWarning,
        props: {
          title: `${this.General.WARNING}`,
          message: `${this.App.ModalRevertNoEffect.CONFIRMATION_MESSAGE}`,
          confirm: async () => {
            if (this.message.length && this.reasonOption) {
              this.isLoading = true;

              const processFileRevertNoEffect = {
                processFileId: this.processFileId,
                revertNoEffectMotivation: this.reasonOption[0],
                revertNoEffectJustification: this.message
              };
              await this.setProcessFileRevertNoEffect(
                processFileRevertNoEffect
              );
              this.isLoading = false;
              this.close();
              this.onClose ? this.onClose() : window.location.reload();
            } else {
              this.snackToast.open({
                message: this.General.TEXTS.AWAITING_CONTENT,
                type: "is-danger",
                duration: 8000
              });
            }
          },
          buttons: [
            { label: General.YES, isConfirm: false, action: true },
            { label: General.NO, isConfirm: true, action: false }
          ]
        },
        hasModalCard: true,
        trapFocus: true
      });
    },

    showModalUpload() {
      this.$buefy.modal.open({
        parent: this,
        component: ModalAttachments,
        props: {
          listProcess: this.listProcess,
          isRevertNoEffect: true,
          currentSizeProcess: this.getCurrentSizeProcess
        },
        hasModalCard: true,
        trapFocus: true
      });
    },

    close() {
      this.clearSelection();
      this.$parent.close();
    },

    async loadProcessFile() {
      await this.processFileRequest({
        processId: this.processId,
        page: this.currentPageNoEditable,
        paginator_count: ATTACHEMENT_MAX_COUNT,
        type: "noeffect"
      });
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables";

.modal-card {
  display: flex;
  justify-content: space-between;
  width: 60vw;
  max-width: 960px;
  max-height: 75vh;
  min-height: 500px;
  background-color: $color-1;

  @media only screen and (max-width: $tablet) {
    width: 100%;
  }

  .optional-label {
    font-size: 12px;
    font-weight: 300;
    color: $color-13;
  }
}

.header-body {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-around;
  border-bottom: 1px solid;
  border-bottom-color: #cdcdcd;
}

.card-body {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid;
  border-bottom-color: #cdcdcd;
}

.modal-content-container {
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  justify-content: space-between;
  overflow: auto;
}

.custom-width {
  width: 98%;
  margin-bottom: 15px;
}

table tbody tr:hover {
  background-color: transparent !important;
}

.custom-align-left {
  justify-content: flex-end;
}
</style>
